// src/routes/RouteGuards.js
import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const roleDashboard = (role) => {
  switch ((role || "").toString().toLowerCase()) {
    case "admin":
    case "secretary":
      return "/admin";
    case "district":
      return "/district";
    case "player":
      return "/player";
    case "coach":
      return "/coach";
    default:
      return "/";
  }
};

const getStoredAuth = () => {
  if (typeof window === "undefined") return { token: null, user: null, role: null };
  try {
    const token = sessionStorage.getItem("token");
    const role = sessionStorage.getItem("role");
    const rawUser = sessionStorage.getItem("user");
    const user = rawUser ? JSON.parse(rawUser) : null;
    return { token, user, role };
  } catch {
    return { token: null, user: null, role: null };
  }
};

/**
 * PublicRoute:
 * - If user is authenticated, redirect to their role dashboard
 * - Otherwise render children (public page)
 */
export const PublicRoute = ({ children, allowAuthenticated = false }) => {
  const { token, user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (allowAuthenticated) return children;

  const stored = getStoredAuth();
  const effectiveToken = token || stored.token;
  const effectiveUser = user || stored.user;

  if (effectiveToken && effectiveUser) {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("switch") === "true") {
      return children;
    }

    const target = roleDashboard(effectiveUser.role || stored.role);

    // Avoid redirecting to the same path (prevents re-render loops)
    if (location.pathname !== target) {
      return <Navigate to={target} replace />;
    } else {
      return children;
    }
  }

  return children;
};

/**
 * ProtectedRoute:
 * - If not authenticated, redirect to /login
 * - If authenticated but role not allowed, redirect to their own dashboard
 * - Otherwise render children
 */
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { token, user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;

  const stored = getStoredAuth();
  const effectiveToken = token || stored.token;
  const effectiveUser = user || stored.user;

  if (!effectiveToken || !effectiveUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const role = (effectiveUser.role || stored.role || "").toString().toLowerCase();
    const allowed = allowedRoles.map((r) => r.toString().toLowerCase());
    if (!allowed.includes(role)) {
      // Send to their correct dashboard instead of /login
      return <Navigate to={roleDashboard(role)} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
